import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Card, Table, Tag, Button, Space, Modal, message } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons'
import PageHeader from '@/components/PageHeader'
import { SelectC, InputC, RangePickerC } from '@/i2antdV5'
import QueryManager from '@/utils/queryManager'
import ApiPollingManager from '@/utils/ApiPollingManager'
import {
  fetchCustomers,
  deleteCustomerAction
} from '@/redux/order/customerSlice'
import { fetchStoreList } from '@/redux/dashboard/storeList/storeSlice'
import { renderTextStr, renderPhoneStr, formatDateTimeStr } from '@/utils/renderUtils'
import CustomerModal from './share/CustomerModal'
import CustomerDetailDrawer from './share/CustomerDetailDrawer'

const breadcrumbList = [
  { breadcrumbName: '首页', href: '/client/commandCenter' },
  { breadcrumbName: '订单管理', href: '/client/order/orderList' },
  { breadcrumbName: '客户管理' }
]

// 客户标签配置
const TAG_CONFIG = {
  VIP: { text: 'VIP', color: 'red' },
  新: { text: '新客', color: 'blue' },
  旧: { text: '旧客', color: 'green' }
}

// 渠道配置
const CHANNEL_CONFIG = {
  xiaohongshu: { text: '小红书', color: 'red' },
  douyin: { text: '抖音', color: 'blue' },
  weixin: { text: '微信', color: 'green' },
  other: { text: '其他', color: 'default' }
}

// 获取默认查询参数
const getDefaultQuery = () => {
  let storeId = ''
  try {
    const { search } = window.location || {}
    if (search) {
      const params = new URLSearchParams(search)
      const storeIdParam = params.get('store_id') || params.get('storeId')
      if (storeIdParam) {
        const parsed = parseInt(storeIdParam, 10)
        if (!Number.isNaN(parsed)) {
          storeId = String(parsed)
        }
      }
    }
  } catch (e) {
    // 容错处理
  }
  return {
    storeId: storeId,
    page: 1,
    pageSize: 20
  }
}

const defaultQuery = getDefaultQuery()

/**
 * 接口配置管理
 */
const getAPIConfig = (componentInstance) => ({
  // 只需加载一次的接口配置
  once: [
    {
      name: 'fetchStoreList',
      dispatch: fetchStoreList,
      getParams: () => ({
        serviceType: 'default',
        dataType: 'storeOption'
      })
    }
  ],
  // 实时获取接口配置
  realtime: [],
  // 定时获取接口配置
  polling: [],
  // 手动触发接口
  manual: [
    {
      name: 'fetchCustomers',
      dispatch: fetchCustomers,
      getParams: (query) => ({ query: { ...query } })
    }
  ]
})

class CustomerListPage extends React.Component {
  constructor(props) {
    super(props)
    this.lastUpdateTime = 0
    this.lastUpdateKey = null
    
    // 初始化QueryManager
    this.queryManager = new QueryManager(defaultQuery, {
      syncToUrl: true
    })
    
    // 创建 API 轮询管理器
    this.apiManager = new ApiPollingManager(getAPIConfig(this), this)
    
    // 设置更新回调
    this.queryManager.setUpdateCallback((query) => {
      this.apiManager.fetchData(query, true)
    })
    
    // 设置navigate函数
    if (props.navigate) {
      this.queryManager.setNavigate(props.navigate)
    }
  }

  componentDidMount() {
    const { location } = this.props
    const initializedQuery = this.queryManager.initialize(location)
    
    // 重写 fetchData 方法
    const originalFetchData = this.apiManager.fetchData.bind(this.apiManager)
    this.apiManager.fetchData = async (value, showErrorDialog = true) => {
      if (value) {
        const oldCallback = this.queryManager.onUpdateCallback
        this.queryManager.setUpdateCallback(null)
        this.queryManager.updateQuery(value, undefined, { 
          autoFetch: false,
          syncToUrl: this.queryManager.syncToUrl
        })
        this.queryManager.setUpdateCallback(oldCallback)
      }
      const currentQuery = this.queryManager.getQuery()
      this.apiManager.currentQuery = currentQuery
      return originalFetchData(currentQuery, showErrorDialog)
    }
    
    // 初始化管理器
    this.apiManager.currentQuery = initializedQuery
    this.apiManager.init(initializedQuery)
  }

  componentDidUpdate(prevProps) {
    this.apiManager.checkReduxErrors(prevProps, () => ({
      fetchCustomers: this.props.customerObj?.list
    }))
  }

  componentWillUnmount() {
    this.apiManager.cleanup()
  }

  /**
   * 更新query参数
   */
  updateQuery = (keyOrUpdates, value, options = {}) => {
    const isActionCall = typeof keyOrUpdates === 'object' && keyOrUpdates !== null && options.syncToUrl === undefined
    
    const now = Date.now()
    let updateKey = null
    
    if (typeof keyOrUpdates === 'string') {
      updateKey = `${keyOrUpdates}:${JSON.stringify(value)}`
    } else if (typeof keyOrUpdates === 'object' && keyOrUpdates !== null) {
      const keys = Object.keys(keyOrUpdates).sort()
      updateKey = keys.map(k => `${k}:${JSON.stringify(keyOrUpdates[k])}`).join('|')
    }
    
    if (updateKey && 
        this.lastUpdateKey === updateKey && 
        now - this.lastUpdateTime < 300) {
      return this.queryManager.getQuery()
    }
    
    this.lastUpdateTime = now
    this.lastUpdateKey = updateKey
    
    if (typeof keyOrUpdates === 'object' && keyOrUpdates !== null) {
      const currentQuery = this.queryManager.getQuery()
      let hasChange = false
      for (const key in keyOrUpdates) {
        if (currentQuery[key] !== keyOrUpdates[key]) {
          hasChange = true
          break
        }
      }
      if (!hasChange) {
        return currentQuery
      }
    } else if (typeof keyOrUpdates === 'string') {
      const currentQuery = this.queryManager.getQuery()
      if (currentQuery[keyOrUpdates] === value) {
        return currentQuery
      }
    }
    
    const finalOptions = {
      autoFetch: true,
      syncToUrl: options.syncToUrl !== undefined 
        ? options.syncToUrl 
        : (isActionCall ? false : this.queryManager.syncToUrl),
      ...options
    }
    return this.queryManager.updateQuery(keyOrUpdates, value, finalOptions)
  }

  /**
   * 获取当前query参数
   */
  getQuery = () => {
    return this.queryManager.getQuery()
  }

  /**
   * 获取数据
   */
  fetchData = async (value, showErrorDialog = true) => {
    return this.apiManager.fetchData(value, showErrorDialog)
  }

  /**
   * 分页切换
   */
  handlePageChange = (page, pageSize) => {
    this.updateQuery({ page, pageSize })
  }

  /**
   * 保存客户成功回调
   */
  handleSaveSuccess = () => {
    // 刷新列表
    const currentQuery = this.getQuery()
    this.fetchData(currentQuery)
  }

  /**
   * 查看客户详情
   */
  handleViewDetail = (customerId) => {
    // 详情通过抽屉组件显示，不需要跳转
    // 抽屉组件会通过 children 的 onClick 触发显示
  }

  /**
   * 删除客户
   */
  handleDeleteCustomer = (customer) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除客户"${customer.name}"吗？删除后无法恢复。`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          const { dispatch } = this.props
          await dispatch(deleteCustomerAction(customer.id)).unwrap()
          message.success('删除成功')
          // 刷新列表
          const currentQuery = this.getQuery()
          this.fetchData(currentQuery)
        } catch (error) {
          message.error(error || '删除失败')
        }
      }
    })
  }

  /**
   * 获取表格列配置
   */
  getColumns = () => {
    return [
      {
        title: '客户信息',
        key: 'customerInfo',
        width: 180,
        fixed: 'left',
        render: (_, record) => {
          const name = renderTextStr(record.name)
          const phone = renderPhoneStr(record.phone)
          const tags = record.tags || []
          return (
            <div>
              <div style={{ marginBottom: '4px' }}>
                <span>{name}</span>
                {tags && tags.length > 0 && (
                  <Space size="small" style={{ marginLeft: '8px' }}>
                    {tags.map(tag => {
                      const config = TAG_CONFIG[tag] || { text: tag, color: 'default' }
                      return <Tag key={tag} color={config.color} style={{ margin: 0 }}>{config.text}</Tag>
                    })}
                  </Space>
                )}
              </div>
              <div style={{ color: '#999', fontSize: '12px' }}>{phone}</div>
            </div>
          )
        }
      },
      {
        title: '地址',
        dataIndex: 'address',
        key: 'address',
        width: 250,
        ellipsis: true,
        render: (text) => renderTextStr(text)
      },
      {
        title: '经纬度',
        key: 'points',
        width: 180,
        render: (_, record) => {
          const points = record.points || []
          if (points.length >= 2) {
            return `${points[0]}, ${points[1]}`
          }
          return '-'
        }
      },
      {
        title: '渠道',
        dataIndex: 'channel',
        key: 'channel',
        width: 120,
        render: (channel) => {
          if (!channel) {
            return '-'
          }
          const config = CHANNEL_CONFIG[channel] || { text: channel, color: 'default' }
          return <Tag color={config.color}>{config.text}</Tag>
        }
      },
      {
        title: '录入人员',
        dataIndex: 'createdBy',
        key: 'createdBy',
        width: 120,
        render: (text) => renderTextStr(text)
      },
      {
        title: '门店',
        dataIndex: 'storeName',
        key: 'storeName',
        width: 180,
        render: (text) => renderTextStr(text)
      },
      {
        title: '创建时间',
        dataIndex: 'createTime',
        key: 'createTime',
        width: 160,
        render: (text) => formatDateTimeStr(text)
      },
      {
        title: '操作',
        key: 'action',
        width: 200,
        fixed: 'right',
        render: (_, record) => {
          return (
            <Space size="small">
              <CustomerDetailDrawer
                baseProps={{
                  customerId: record.id,
                  onClose: () => {
                    // 可以在这里处理关闭后的逻辑，比如刷新列表
                  }
                }}
              >
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                >
                  详情
                </Button>
              </CustomerDetailDrawer>
              <CustomerModal
                baseProps={{
                  editingCustomer: record,
                  onSuccess: this.handleSaveSuccess
                }}
              >
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                >
                  编辑
                </Button>
              </CustomerModal>
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => this.handleDeleteCustomer(record)}
              >
                删除
              </Button>
            </Space>
          )
        }
      }
    ]
  }

  render() {
    const { storeObj, customerObj } = this.props
    const { default_storeOption: storeOption } = storeObj || {}
    const { list: customerListData } = customerObj || {}
    const { fetching, data, query } = customerListData || {}
    
    const currentQuery = this.getQuery()
    const { list = [], total = 0, pageNumber = 1, pageSize = 20 } = data || {}
    
    // 客户标签选项
    const tagOptions = [
      { id: 'VIP', value: 'VIP' },
      { id: '新', value: '新客' },
      { id: '旧', value: '旧客' }
    ]
    
    // 渠道选项
    const channelOptions = [
      { id: 'xiaohongshu', value: '小红书' },
      { id: 'douyin', value: '抖音' },
      { id: 'weixin', value: '微信' },
      { id: 'other', value: '其他' }
    ]

    return (
      <div>
        <PageHeader
          breadcrumbList={breadcrumbList}
        />
        <div className="i2-container">
          <Card className="i2-card-page">
            <section className="i2-app-toolbar">
              <div className="toolbar-filters">
                <InputC
                  label="搜索"
                  query={currentQuery}
                  searchKey="keyword"
                  action={this.updateQuery}
                  placeholder="请输入客户姓名或手机号"
                />
                <SelectC
                  label="门店"
                  query={currentQuery}
                  searchKey="storeId"
                  options={storeOption || []}
                  action={this.updateQuery}
                  placeholder="请选择门店"
                />
                <SelectC
                  label="客户标签"
                  query={currentQuery}
                  searchKey="tags"
                  options={tagOptions}
                  action={this.updateQuery}
                  placeholder="请选择客户标签"
                  modalProps={{
                    mode: 'multiple',
                    style: { width: 200 },
                  }}
                />
                <SelectC
                  label="渠道"
                  query={currentQuery}
                  searchKey="channel"
                  options={channelOptions}
                  action={this.updateQuery}
                  placeholder="请选择渠道"
                />
                <RangePickerC
                  label="时间范围"
                  query={currentQuery}
                  searchKey={['startTime', 'endTime']}
                  action={this.updateQuery}
                  placeholder={['开始时间', '结束时间']}
                />
              </div>
              <div className="toolbar-actions">
                <CustomerModal
                  baseProps={{
                    editingCustomer: null,
                    onSuccess: this.handleSaveSuccess
                  }}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                  >
                    新建客户
                  </Button>
                </CustomerModal>
              </div>
            </section>
            <Table
              loading={fetching}
              columns={this.getColumns()}
              dataSource={list}
              rowKey="id"
              scroll={{ x: 1500 }}
              size="small"
              pagination={{
                current: pageNumber,
                pageSize: pageSize,
                total: total,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条`,
                onChange: this.handlePageChange,
                onShowSizeChange: (current, size) => this.handlePageChange(1, size)
              }}
            />
          </Card>
        </div>
      </div>
    )
  }
}

const CustomerListPageWrapper = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  
  const customerState = useSelector(state => state.order?.customer || {})
  const dashboardState = useSelector(state => state.dashboard || {})
  
  const props = {
    dispatch,
    location,
    navigate,
    storeObj: dashboardState.storeList || {},
    customerObj: customerState
  }
  
  return <CustomerListPage {...props} />
}

export default CustomerListPageWrapper

